import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Plus,
  Trash2,
  Image as ImageIcon,
  Save,
  Eye,
  Upload,
  Download,
  Edit,
  Copy,
  GripVertical,
} from 'lucide-react';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';
import { toast } from 'sonner';
import { DataKeyHelper } from './DataKeyHelper';
import { generateCustomPLCReport } from '../../utils/advanced-pdf-generator';

interface TemplateColumn {
  id: string;
  label: string;
  dataKey: string;
  width?: string;
  format?: 'text' | 'number' | 'date' | 'time' | 'datetime' | 'kw' | 'kwh';
}

interface TemplateSection {
  id: string;
  title: string;
  columns: TemplateColumn[];
}

interface PDFTemplate {
  // Header
  reportTitle: string;
  reportSubtitle: string;
  leftLogo?: string;
  rightLogo?: string;

  // Company Info
  companyFields: {
    label: string;
    value: string;
  }[];

  // Report Info
  showDateRange: boolean;
  dateRangeLabel: string;
  reportIntervalValue?: number;
  reportIntervalUnit?: 'seconds' | 'minutes' | 'hours';

  // Data Sections
  sections: TemplateSection[];

  // Footer
  showCheckedBy: boolean;
  showVerifiedBy: boolean;
  showGeneratedBy: boolean;
  footerText?: string;
  includeCharts?: boolean;

  // Styling
  primaryColor: string;
  borderStyle: 'single' | 'double' | 'none';
  fontSize: 'small' | 'medium' | 'large';
}

export function AdvancedPDFTemplateEditor() {
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [template, setTemplate] = useState<PDFTemplate>({
    reportTitle: 'NEW WINTERIZATION ENERGY METER DATA LOG REPORT',
    reportSubtitle: '',
    companyFields: [
      { label: 'COMPANY NAME', value: '{companyName}' },
      { label: 'USER NAME', value: '{downloadedBy}' },
    ],
    showDateRange: true,
    dateRangeLabel: 'Report',
    reportIntervalValue: 0,
    reportIntervalUnit: 'minutes',
    sections: [
      {
        id: 'section1',
        title: 'NEW WINTERIZ. GND. FLR. MPM-17',
        columns: [
          { id: 'col1', label: 'ACTIVE POWER', dataKey: 'activePower1', format: 'kw', width: '15%' },
          { id: 'col2', label: 'TOTAL POWER CONSUMP.', dataKey: 'totalPower1', format: 'kwh', width: '15%' },
        ],
      },
      {
        id: 'section2',
        title: 'OLD WINTERIZ. 3rd FLOOR MPM-18',
        columns: [
          { id: 'col3', label: 'ACTIVE POWER', dataKey: 'activePower2', format: 'kw', width: '15%' },
          { id: 'col4', label: 'TOTAL POWER CONSUMP.', dataKey: 'totalPower2', format: 'kwh', width: '15%' },
        ],
      },
    ],
    showCheckedBy: true,
    showVerifiedBy: true,
    showGeneratedBy: true,
    includeCharts: false,
    primaryColor: '#000000',
    borderStyle: 'single',
    fontSize: 'small',
  });

  const session = sessionStorage.get();
  const token = session?.token || '';

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadTemplate();
    }
  }, [selectedClient]);

  const loadClients = async () => {
    try {
      const data = await adminApi.getClients(token);
      setClients(data);
      if (data.length > 0) {
        setSelectedClient(data[0].id);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const loadTemplate = async () => {
    if (!selectedClient) return;

    try {
      setLoading(true);
      const data = await adminApi.getPDFTemplate(token, selectedClient);
      if (data && data.advancedTemplate) {
        const incoming = data.advancedTemplate as Partial<PDFTemplate>;
        setTemplate((prev) => ({
          ...prev,
          ...incoming,
          reportIntervalValue: Number(incoming.reportIntervalValue ?? prev.reportIntervalValue ?? 0) || 0,
          reportIntervalUnit: (incoming.reportIntervalUnit === 'seconds' || incoming.reportIntervalUnit === 'hours' || incoming.reportIntervalUnit === 'minutes')
            ? incoming.reportIntervalUnit
            : (prev.reportIntervalUnit || 'minutes'),
        }));
      }
    } catch (error: any) {
      console.error('Error loading template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedClient) return;

    try {
      setSaving(true);
      await adminApi.updatePDFTemplate(token, selectedClient, {
        advancedTemplate: template,
      });
      toast.success('Template saved successfully');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (type: 'left' | 'right', file: File) => {
    const MAX_SIZE_MB = 5;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`Logo must be less than ${MAX_SIZE_MB}MB.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const rawBase64 = e.target?.result as string;

      // Auto-compress if raw file is larger than 500 KB to keep the template JSON manageable
      if (file.size > 500 * 1024) {
        const img = new Image();
        img.onload = () => {
          const MAX_W = 400;
          const MAX_H = 200;
          let { width, height } = img;
          const ratio = Math.min(MAX_W / width, MAX_H / height, 1);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0, width, height);
          const compressed = canvas.toDataURL('image/jpeg', 0.85);

          setTemplate(prev => ({
            ...prev,
            [type === 'left' ? 'leftLogo' : 'rightLogo']: compressed,
          }));
          toast.success(`${type === 'left' ? 'Left' : 'Right'} logo uploaded & auto-compressed`);
        };
        img.src = rawBase64;
      } else {
        setTemplate(prev => ({
          ...prev,
          [type === 'left' ? 'leftLogo' : 'rightLogo']: rawBase64,
        }));
        toast.success(`${type === 'left' ? 'Left' : 'Right'} logo uploaded`);
      }
    };
    reader.readAsDataURL(file);
  };


  const addCompanyField = () => {
    setTemplate(prev => ({
      ...prev,
      companyFields: [...prev.companyFields, { label: '', value: '' }],
    }));
  };

  const updateCompanyField = (index: number, field: 'label' | 'value', value: string) => {
    setTemplate(prev => ({
      ...prev,
      companyFields: prev.companyFields.map((f, i) =>
        i === index ? { ...f, [field]: value } : f
      ),
    }));
  };

  const removeCompanyField = (index: number) => {
    setTemplate(prev => ({
      ...prev,
      companyFields: prev.companyFields.filter((_, i) => i !== index),
    }));
  };

  const addSection = () => {
    const newId = `section${template.sections.length + 1}`;
    setTemplate(prev => ({
      ...prev,
      sections: [...prev.sections, {
        id: newId,
        title: 'New Section',
        columns: [],
      }],
    }));
  };

  const updateSection = (sectionId: string, updates: Partial<TemplateSection>) => {
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId ? { ...s, ...updates } : s
      ),
    }));
  };

  const removeSection = (sectionId: string) => {
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId),
    }));
  };

  const addColumn = (sectionId: string) => {
    const section = template.sections.find(s => s.id === sectionId);
    if (!section) return;

    const newId = `col${Date.now()}`;
    updateSection(sectionId, {
      columns: [...section.columns, {
        id: newId,
        label: 'New Column',
        dataKey: newId,
        format: 'text',
        width: '15%',
      }],
    });
  };

  const updateColumn = (sectionId: string, columnId: string, updates: Partial<TemplateColumn>) => {
    const section = template.sections.find(s => s.id === sectionId);
    if (!section) return;

    updateSection(sectionId, {
      columns: section.columns.map(c =>
        c.id === columnId ? { ...c, ...updates } : c
      ),
    });
  };

  const removeColumn = (sectionId: string, columnId: string) => {
    const section = template.sections.find(s => s.id === sectionId);
    if (!section) return;

    updateSection(sectionId, {
      columns: section.columns.filter(c => c.id !== columnId),
    });
  };

  const handlePreview = async () => {
    try {
      toast.success('Generating preview...');
      const previewEndDate = new Date();
      const previewStartDate = new Date(previewEndDate.getTime() - 24 * 60 * 60 * 1000);

      const [liveRowsRaw, historicalResult] = await Promise.all([
        adminApi.getLiveData(token, selectedClient).catch(() => []),
        adminApi.getHistoricalData(token, selectedClient, {
          startDate: previewStartDate.toISOString(),
          endDate: previewEndDate.toISOString(),
          limit: 500,
        }).catch(() => ({ data: [] })),
      ]);

      const liveRows = Array.isArray(liveRowsRaw) ? liveRowsRaw : [];
      const historicalRows = Array.isArray(historicalResult?.data) ? historicalResult.data : [];

      if (liveRows.length === 0 && historicalRows.length === 0) {
        toast.info('No recent live or historical records found. Preview will render template structure only.');
      }

      const pdfBlob = await generateCustomPLCReport(
        liveRows,
        historicalRows,
        template,
        clients.find(c => c.id === selectedClient)?.name || 'Client',
        previewStartDate,
        previewEndDate
      );

      const url = URL.createObjectURL(pdfBlob);
      setPreviewUrl(url);
      setShowPreview(true);
      toast.success('Preview generated!');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate preview');
    }
  };

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Advanced PDF Template Editor</h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Create custom report layouts with logos, company fields, and dynamic columns
          </p>
        </div>

        <div className="flex w-full sm:w-auto flex-col sm:flex-row gap-3">
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-border rounded-md bg-background text-foreground"
          >
            <option value="">Select Client</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <Button onClick={handlePreview} disabled={!selectedClient} variant="outline">
            <Eye className="h-4 w-4 mr-2" />
            Preview PDF
          </Button>

          <Button onClick={handleSave} disabled={saving || !selectedClient}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>

      {/* PDF Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={(open) => {
        setShowPreview(open);
        if (!open && previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>PDF Template Preview</DialogTitle>
          </DialogHeader>
          <div className="w-full h-[calc(90vh-8rem)] overflow-auto">
            {previewUrl && (
              <iframe
                src={previewUrl}
                className="w-full h-full border-0"
                title="PDF Preview"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {selectedClient && (
        <>
          <Tabs defaultValue="header" className="w-full">
            <TabsList className="w-full sm:grid sm:grid-cols-4 border border-border/70 bg-muted/80">
              <TabsTrigger value="header">Header & Logos</TabsTrigger>
              <TabsTrigger value="info">Company Info</TabsTrigger>
              <TabsTrigger value="data">Data Columns</TabsTrigger>
              <TabsTrigger value="footer">Footer</TabsTrigger>
            </TabsList>

            <TabsContent value="header" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Report Header</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Report Title</Label>
                    <Input
                      value={template.reportTitle}
                      onChange={(e) => setTemplate({ ...template, reportTitle: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Left Logo</Label>
                      <div className="border rounded-lg p-4 text-center">
                        {template.leftLogo ? (
                          <div>
                            <img src={template.leftLogo} alt="Left Logo" className="h-20 mx-auto mb-2" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setTemplate({ ...template, leftLogo: undefined })}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleLogoUpload('left', file);
                              }}
                            />
                            <div className="flex flex-col items-center gap-2">
                              <Upload className="h-8 w-8 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">Upload Left Logo</span>
                            </div>
                          </label>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Right Logo</Label>
                      <div className="border rounded-lg p-4 text-center">
                        {template.rightLogo ? (
                          <div>
                            <img src={template.rightLogo} alt="Right Logo" className="h-20 mx-auto mb-2" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setTemplate({ ...template, rightLogo: undefined })}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleLogoUpload('right', file);
                              }}
                            />
                            <div className="flex flex-col items-center gap-2">
                              <Upload className="h-8 w-8 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">Upload Right Logo</span>
                            </div>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="info" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Company Information Fields</CardTitle>
                    <Button onClick={addCompanyField} size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Field
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {template.companyFields.map((field, index) => (
                    <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                      <Input
                        placeholder="Label (e.g., COMPANY NAME)"
                        value={field.label}
                        onChange={(e) => updateCompanyField(index, 'label', e.target.value)}
                      />
                      <Input
                        placeholder="Value (e.g., Your Company Ltd.)"
                        value={field.value}
                        onChange={(e) => updateCompanyField(index, 'value', e.target.value)}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeCompanyField(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Historical Report Interval</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    This controls report grouping interval for client Historical Reports.
                    Set 0 for raw data (no grouping).
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3">
                    <div className="space-y-2">
                      <Label>Interval Value</Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={template.reportIntervalValue ?? 0}
                        onChange={(e) => {
                          const parsed = Math.max(0, Math.floor(Number(e.target.value) || 0));
                          setTemplate({ ...template, reportIntervalValue: parsed });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <select
                        value={template.reportIntervalUnit || 'minutes'}
                        onChange={(e) => setTemplate({ ...template, reportIntervalUnit: e.target.value as 'seconds' | 'minutes' | 'hours' })}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                      >
                        <option value="seconds">Seconds</option>
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="data" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Data Sections (PLC/Meter Groups)</CardTitle>
                    <Button onClick={addSection} size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Section
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {template.sections.map((section) => (
                    <div key={section.id} className="border rounded-lg p-4 space-y-4">
                      <div className="flex items-center gap-2">
                        <Input
                          value={section.title}
                          onChange={(e) => updateSection(section.id, { title: e.target.value })}
                          placeholder="Section Title (e.g., NEW WINTERIZ. GND. FLR. MPM-17)"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addColumn(section.id)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Column
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => removeSection(section.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="space-y-2">
                        {section.columns.map((column) => (
                          <div key={column.id} className="grid grid-cols-[2fr_2fr_1fr_1fr_auto] gap-2 items-center">
                            <Input
                              placeholder="Column Label"
                              value={column.label}
                              onChange={(e) => updateColumn(section.id, column.id, { label: e.target.value })}
                            />
                            <Input
                              placeholder="Data Key"
                              value={column.dataKey}
                              onChange={(e) => updateColumn(section.id, column.id, { dataKey: e.target.value })}
                            />
                            <select
                              value={column.format}
                              onChange={(e) => updateColumn(section.id, column.id, { format: e.target.value as any })}
                              className="px-2 py-1 border border-border rounded bg-background text-foreground"
                            >
                              <option value="text">Text</option>
                              <option value="number">Number</option>
                              <option value="kw">KW</option>
                              <option value="kwh">KWH</option>
                              <option value="date">Date</option>
                              <option value="time">Time</option>
                            </select>
                            <Input
                              placeholder="Width %"
                              value={column.width || ''}
                              onChange={(e) => updateColumn(section.id, column.id, { width: e.target.value })}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeColumn(section.id, column.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="footer" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Footer Configuration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Show "Checked By" field</Label>
                    <input
                      type="checkbox"
                      checked={template.showCheckedBy}
                      onChange={(e) => setTemplate({ ...template, showCheckedBy: e.target.checked })}
                      className="h-5 w-5"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Show "Verified By" field</Label>
                    <input
                      type="checkbox"
                      checked={template.showVerifiedBy}
                      onChange={(e) => setTemplate({ ...template, showVerifiedBy: e.target.checked })}
                      className="h-5 w-5"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Show "Generated By" field</Label>
                    <input
                      type="checkbox"
                      checked={template.showGeneratedBy}
                      onChange={(e) => setTemplate({ ...template, showGeneratedBy: e.target.checked })}
                      className="h-5 w-5"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Include Charts in PDF</Label>
                    <input
                      type="checkbox"
                      checked={!!template.includeCharts}
                      onChange={(e) => setTemplate({ ...template, includeCharts: e.target.checked })}
                      className="h-5 w-5"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <DataKeyHelper />
        </>
      )}
    </div>
  );
}
